import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getDictionary, getDictionaryMeta } from "../util/wordywhiz";

const PRESETS = [
  { label: "Contains Q without U", value: "q_no_u" },
  { label: "Palindromes", value: "palindrome" },
  { label: "Words without Vowels", value: "no_vowels" },
  { label: "7 letter words with 5+ vowels", value: "7l5v" },
  { label: "5 letter words with 4 vowels", value: "5l4v" },
  { label: "7 letter words containing S-A-T-I-N-E", value: "satine" },
  { label: "Ends in Z", value: "endinz" },
];

function DictionaryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dict, setDict] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isStartingDrill, setIsStartingDrill] = useState(false);
  const [error, setError] = useState("");

  const [userInput, setUserInput] = useState({
    presets: [],
    includes: "",
    excludes: "",
    wordLen: "",
    timeBetweenWords: "2",
    textToSpeech: false,
    shuffle: false,
  });

  const [drillMinLen, setDrillMinLen] = useState("3");
  const [drillStrategy, setDrillStrategy] = useState("substring");

  useEffect(() => {
    getDictionaryMeta(id)
      .then((data) => setDict(data))
      .catch(() => setError("Could not load dictionary."))
      .finally(() => setIsLoading(false));
  }, [id]);

  function togglePreset(value) {
    setUserInput((curr) => {
      const presets = curr.presets.includes(value)
        ? curr.presets.filter((p) => p !== value)
        : [...curr.presets, value];
      return { ...curr, presets };
    });
  }

  function startStudy() {
    setIsStarting(true);
    getDictionary(id)
      .then((fullDict) => navigate("/wordy-whiz/study", { state: { dict: fullDict, userInput } }))
      .catch(() => setError("Could not load word list."))
      .finally(() => setIsStarting(false));
  }

  function startDrill() {
    setIsStartingDrill(true);
    getDictionary(id)
      .then((fullDict) => {
        const words = JSON.parse(fullDict.words_full).flat();
        navigate("/wordy-whiz/stacks/drill", {
          state: {
            words,
            dictTitle: dict?.title,
            minWordLen: drillMinLen,
            matchStrategy: drillStrategy,
          },
        });
      })
      .catch(() => setError("Could not load word list."))
      .finally(() => setIsStartingDrill(false));
  }

  const preview = dict?.preview ? JSON.parse(dict.preview) : [];

  return (
    <div className="study-page">
      <div className="study-header">
        <p className="study-eyebrow">Wordy Whiz · Learn</p>
        <h1>{dict?.title || "Dictionary"}</h1>
        {dict?.description && <p>{dict.description}</p>}
      </div>

      {isLoading && <div className="dict-spinner" />}
      {error && <p className="dict-state dict-state--error">{error}</p>}

      {!isLoading && !error && dict && (
        <>
          <div className="dict-config">
            {preview.length > 0 && (
              <div className="dict-config__preview">
                <p className="dict-config__preview-label">Preview</p>
                <p className="dict-config__preview-words">{preview.join(", ")}</p>
              </div>
            )}

            <p className="dict-config__hint">
              All filters are optional. Without filters, all words are shown.
            </p>

            <p className="dict-config__section">Preset filters</p>
            <div className="dict-config__presets">
              {PRESETS.map(({ label, value }) => (
                <button
                  key={value}
                  type="button"
                  className={`dict-chip${userInput.presets.includes(value) ? " active" : ""}`}
                  aria-pressed={userInput.presets.includes(value)}
                  onClick={() => togglePreset(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            <p className="dict-config__section">Custom filters</p>
            <div className="dict-config__grid">
              <label className="dict-field">
                <span className="dict-field__label">Includes</span>
                <span className="dict-field__hint">
                  Only words containing these strings (comma-separated)
                </span>
                <input
                  className="dict-input"
                  placeholder="a, b, cat"
                  autoCapitalize="none"
                  value={userInput.includes}
                  onChange={(e) => setUserInput((c) => ({ ...c, includes: e.target.value }))}
                />
              </label>
              <label className="dict-field">
                <span className="dict-field__label">Excludes</span>
                <span className="dict-field__hint">
                  Only words NOT containing these strings (comma-separated)
                </span>
                <input
                  className="dict-input"
                  placeholder="a, b, cat"
                  autoCapitalize="none"
                  value={userInput.excludes}
                  onChange={(e) => setUserInput((c) => ({ ...c, excludes: e.target.value }))}
                />
              </label>
            </div>

            <div className="dict-config__grid dict-config__grid--narrow">
              <label className="dict-field">
                <span className="dict-field__label">Word Length</span>
                <input
                  type="number"
                  min="1"
                  className="dict-input"
                  autoCapitalize="none"
                  value={userInput.wordLen}
                  onChange={(e) => setUserInput((c) => ({ ...c, wordLen: e.target.value }))}
                />
              </label>
              <label className="dict-field">
                <span className="dict-field__label">Seconds Per Word</span>
                <input
                  type="number"
                  min="1"
                  className="dict-input"
                  value={userInput.timeBetweenWords}
                  onChange={(e) => setUserInput((c) => ({ ...c, timeBetweenWords: e.target.value }))}
                />
              </label>
            </div>

            <label className="dict-check">
              <input
                type="checkbox"
                checked={userInput.textToSpeech}
                onChange={(e) => setUserInput((c) => ({ ...c, textToSpeech: e.target.checked }))}
              />
              Read words aloud
            </label>
            <label className="dict-check">
              <input
                type="checkbox"
                checked={userInput.shuffle}
                onChange={(e) => setUserInput((c) => ({ ...c, shuffle: e.target.checked }))}
              />
              Shuffle words
            </label>

            <div className="dict-config__actions">
              <button className="ww-btn ww-btn--primary" onClick={startStudy} disabled={isStarting}>
                {isStarting ? "Loading…" : "Create List →"}
              </button>
              <button className="ww-btn ww-btn--secondary" onClick={() => navigate(-1)}>
                Back
              </button>
            </div>
          </div>

          <div className="dict-config dict-config--drill">
            <p className="dict-config__section">Practice on the board</p>
            <p className="dict-config__hint">
              Play Wordy Whiz Stacks using only the words in this list. Good
              practice for spelling bee or Scrabble study.
            </p>

            <div className="dict-config__grid">
              <label className="dict-field">
                <span className="dict-field__label">Minimum Word Length</span>
                <input
                  type="number"
                  min="3"
                  className="dict-input"
                  value={drillMinLen}
                  onChange={(e) => setDrillMinLen(e.target.value)}
                />
              </label>
              <div className="dict-field">
                <span className="dict-field__label">Rules</span>
                <div className="dict-config__presets">
                  <button
                    type="button"
                    className={`dict-chip${drillStrategy === "substring" ? " active" : ""}`}
                    aria-pressed={drillStrategy === "substring"}
                    onClick={() => setDrillStrategy("substring")}
                  >
                    Classic
                  </button>
                  <button
                    type="button"
                    className={`dict-chip${drillStrategy === "exact" ? " active" : ""}`}
                    aria-pressed={drillStrategy === "exact"}
                    onClick={() => setDrillStrategy("exact")}
                  >
                    Challenge
                  </button>
                </div>
                <span className="dict-field__hint">
                  {drillStrategy === "exact"
                    ? "Challenge: the whole row or column must be a real word, no partial credit."
                    : "Classic: any valid word within the row or column clears, partial credit counts."}
                </span>
              </div>
            </div>

            <div className="dict-config__actions">
              <button className="ww-btn ww-btn--primary" onClick={startDrill} disabled={isStartingDrill}>
                {isStartingDrill ? "Loading…" : "Practice This List →"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DictionaryDetail;
